// client/src/components/pages/CustomSummaries/DataVisualization.tsx
//
// The result view of a custom summary: a table, or a line / bar / pie chart of the rows.
// The line view draws a gradient area under every left-axis series and a line for every
// series. It is a ComposedChart: recharts 2's AreaChart only draws <Area>, so as an AreaChart
// the lines (and every right-axis series, which has no area) were silently left out (ST-00260).
// The areas stay out of the tooltip and legend (tooltipType / legendType "none"), which list
// each series once, by its line.
// Converted from DataVisualization.js in the same change (CLAUDE.md TypeScript rule).
import { useState, useMemo, type ReactNode } from 'react';
import {
  FaTable,
  FaChartBar,
  FaChartLine,
  FaChartPie,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaSearch,
  FaChevronLeft,
  FaChevronRight,
} from 'react-icons/fa';
import {
  ComposedChart,
  Line,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import ThemedSelect from '../../common/ThemedSelect';
import { useThemeTokens } from '../../../utils/themeTokens';
import type {
  CustomSummaryChartConfig,
  CustomSummaryRow,
  CustomSummaryVisualizationType,
} from '../../../types/clientPages/customSummaries';

interface SeriesColour {
  stroke: string;
  fill: string;
}

// Refined palette — muted, modern tones that work well together.
// Light uses deep 800/900 hues; dark uses the 400-level of the SAME hues so
// series stay distinguishable against a dark surface (ST-00127).
const PALETTE_LIGHT: SeriesColour[] = [
  { stroke: '#312e81', fill: 'rgba(49,46,129,0.25)' }, // indigo
  { stroke: '#075985', fill: 'rgba(7,89,133,0.25)' }, // sky
  { stroke: '#065f46', fill: 'rgba(6,95,70,0.25)' }, // emerald
  { stroke: '#92400e', fill: 'rgba(146,64,14,0.25)' }, // amber
  { stroke: '#991b1b', fill: 'rgba(153,27,27,0.25)' }, // red
  { stroke: '#5b21b6', fill: 'rgba(91,33,182,0.25)' }, // violet
  { stroke: '#9d174d', fill: 'rgba(157,23,77,0.25)' }, // pink
  { stroke: '#115e59', fill: 'rgba(17,94,89,0.25)' }, // teal
  { stroke: '#9a3412', fill: 'rgba(154,52,18,0.25)' }, // orange
  { stroke: '#1e293b', fill: 'rgba(30,41,59,0.25)' }, // slate
];

const PALETTE_DARK: SeriesColour[] = [
  { stroke: '#818cf8', fill: 'rgba(129,140,248,0.25)' }, // indigo
  { stroke: '#38bdf8', fill: 'rgba(56,189,248,0.25)' }, // sky
  { stroke: '#34d399', fill: 'rgba(52,211,153,0.25)' }, // emerald
  { stroke: '#fbbf24', fill: 'rgba(251,191,36,0.25)' }, // amber
  { stroke: '#f87171', fill: 'rgba(248,113,113,0.25)' }, // red
  { stroke: '#a78bfa', fill: 'rgba(167,139,250,0.25)' }, // violet
  { stroke: '#f472b6', fill: 'rgba(244,114,182,0.25)' }, // pink
  { stroke: '#2dd4bf', fill: 'rgba(45,212,191,0.25)' }, // teal
  { stroke: '#fb923c', fill: 'rgba(251,146,60,0.25)' }, // orange
  { stroke: '#94a3b8', fill: 'rgba(148,163,184,0.25)' }, // slate
];

const PIE_COLORS_LIGHT = [
  '#312e81',
  '#075985',
  '#065f46',
  '#92400e',
  '#991b1b',
  '#5b21b6',
  '#9d174d',
  '#115e59',
  '#9a3412',
  '#1e293b',
];

const PIE_COLORS_DARK = [
  '#818cf8',
  '#38bdf8',
  '#34d399',
  '#fbbf24',
  '#f87171',
  '#a78bfa',
  '#f472b6',
  '#2dd4bf',
  '#fb923c',
  '#94a3b8',
];

/** A tooltip row as recharts hands it to `content`; `type` is the series' tooltipType. */
interface TooltipEntry {
  color?: string;
  name?: string | number;
  value?: unknown;
  type?: string;
}

/** What recharts clones onto the Tooltip `content` element; `labelFormatter` is ours. */
interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipEntry[];
  label?: unknown;
  labelFormatter?: (label: unknown) => string;
}

/** A legend row as recharts hands it to `content`; `payload` is the series' props. */
interface LegendEntry {
  color?: string;
  value?: ReactNode;
  type?: string;
  payload?: { strokeDasharray?: string | number };
}

/** Pie slice geometry recharts passes to the `label` renderer. */
interface PieLabelProps {
  cx: number;
  cy: number;
  midAngle: number;
  innerRadius: number;
  outerRadius: number;
  percent: number;
  name?: unknown;
}

// Custom tooltip with polished styling
const CustomTooltip = ({ active, payload, label, labelFormatter }: CustomTooltipProps) => {
  // A series marked tooltipType="none" (the line view's area fills) is not listed.
  const entries = (payload || []).filter((entry) => entry.type !== 'none');
  if (!active || entries.length === 0) return null;

  const formattedLabel = labelFormatter ? labelFormatter(label) : String(label ?? '');

  return (
    <div className="bg-surface/95 backdrop-blur-sm rounded-xl shadow-lg border border-gray-200/60 px-4 py-3 max-w-xs">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 border-b border-gray-100 pb-1.5">
        {formattedLabel}
      </p>
      <div className="space-y-1">
        {entries.map((entry, i) => (
          <div key={i} className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-1.5 min-w-0">
              <span
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-xs text-gray-600 truncate">{entry.name}</span>
            </div>
            <span className="text-xs font-semibold text-gray-900 tabular-nums">
              {typeof entry.value === 'number'
                ? entry.value % 1 === 0
                  ? entry.value.toLocaleString()
                  : entry.value.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                : String(entry.value ?? '')}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Custom legend with cleaner styling
const CustomLegend = ({ payload }: { payload?: LegendEntry[] }) => {
  if (!payload) return null;
  return (
    <div className="flex flex-wrap justify-center gap-x-5 gap-y-1 mt-2 px-4">
      {/* A series marked legendType="none" (the line view's area fills) is not listed. */}
      {payload
        .filter((entry) => entry.type !== 'none')
        .map((entry, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <span
              className="w-3 h-[3px] rounded-full"
              style={{
                backgroundColor: entry.color,
                borderBottom: entry.payload?.strokeDasharray
                  ? '2px dashed ' + entry.color
                  : undefined,
                height: entry.payload?.strokeDasharray ? 0 : 3,
              }}
            />
            {entry.payload?.strokeDasharray && (
              <span className="w-3 border-b-2 border-dashed" style={{ borderColor: entry.color }} />
            )}
            {!entry.payload?.strokeDasharray && null}
            <span className="text-xs text-gray-500">{entry.value}</span>
          </div>
        ))}
    </div>
  );
};

export interface DataVisualizationProps {
  data?: CustomSummaryRow[] | null;
  visualizationType?: CustomSummaryVisualizationType;
  chartConfig?: CustomSummaryChartConfig | null;
  title?: string;
}

interface SortConfig {
  key: string | null;
  direction: 'asc' | 'desc';
}

interface ColumnAnalysis {
  labelKey: string | null;
  numericKeys: string[];
}

/** A config axis entry (one column or a list) as a list. */
const asList = (value: string | string[] | undefined): string[] =>
  value ? (Array.isArray(value) ? value : [value]) : [];

/** Entry `i` of a (non-empty) colour list, cycling round it. */
const cycle = <T,>(list: readonly T[], i: number): T => list[i % list.length] as T;

/** 1200 -> "1k"; smaller values with thousands separators. */
const compactTick = (v: number): string =>
  v >= 1000 ? (v / 1000).toFixed(0) + 'k' : v.toLocaleString();

const DataVisualization = ({
  data,
  visualizationType = 'table',
  chartConfig,
  title,
}: DataVisualizationProps) => {
  const t = useThemeTokens();
  const PALETTE = t.isDark ? PALETTE_DARK : PALETTE_LIGHT;
  const PIE_COLORS = t.isDark ? PIE_COLORS_DARK : PIE_COLORS_LIGHT;
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: null, direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Filter and sort data
  const processedData = useMemo((): CustomSummaryRow[] => {
    if (!data || data.length === 0) return [];
    let filtered = data;
    if (searchTerm) {
      filtered = data.filter((row) =>
        Object.values(row).some(
          (value) => value && String(value).toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }
    const sortKey = sortConfig.key;
    if (sortKey) {
      filtered = [...filtered].sort((a, b) => {
        const aVal = a[sortKey];
        const bVal = b[sortKey];
        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
        }
        const aStr = String(aVal).toLowerCase();
        const bStr = String(bVal).toLowerCase();
        if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return filtered;
  }, [data, searchTerm, sortConfig]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return processedData.slice(startIndex, startIndex + pageSize);
  }, [processedData, currentPage, pageSize]);

  const totalPages = Math.ceil(processedData.length / pageSize);

  const handleSort = (key: string) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const getSortIcon = (key: string) => {
    if (sortConfig.key !== key) return <FaSort className="text-gray-300" />;
    return sortConfig.direction === 'asc' ? (
      <FaSortUp className="text-primary-500" />
    ) : (
      <FaSortDown className="text-primary-500" />
    );
  };

  const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'number') {
      return Number.isInteger(value)
        ? value.toLocaleString()
        : value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    if (value instanceof Date) return value.toLocaleDateString();
    return String(value);
  };

  const columnAnalysis = useMemo((): ColumnAnalysis => {
    const firstRow = data?.[0];
    if (!data || !firstRow) return { labelKey: null, numericKeys: [] };
    const columns = Object.keys(firstRow);
    const numericKeys: string[] = [];
    let labelKey: string | null = null;

    if (chartConfig) {
      if (chartConfig.xAxis) labelKey = chartConfig.xAxis;
      if (chartConfig.yAxis) {
        return { labelKey: labelKey || columns[0] || null, numericKeys: asList(chartConfig.yAxis) };
      }
    }

    for (const col of columns) {
      const sampleValues = data
        .slice(0, 20)
        .map((row) => row[col])
        .filter((v) => v != null);
      const allNumeric =
        sampleValues.length > 0 && sampleValues.every((v) => typeof v === 'number');
      if (allNumeric) numericKeys.push(col);
      else if (!labelKey) labelKey = col;
    }

    if (!labelKey && columns[0] !== undefined) labelKey = columns[0];
    if (numericKeys.length === 0 && columns[1] !== undefined) numericKeys.push(columns[1]);
    return { labelKey, numericKeys };
  }, [data, chartConfig]);

  const formatXTick = (value: unknown): string => {
    if (!value) return '';
    const str = String(value);
    if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
      const d = new Date(str);
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    }
    return str.length > 16 ? str.slice(0, 14) + '…' : str;
  };

  const formatXTooltipLabel = (value: unknown): string => {
    if (!value) return '';
    const str = String(value);
    if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
      const d = new Date(str);
      return d.toLocaleDateString('en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    }
    return str;
  };

  // Shared axis styling
  const axisStyle = { fontSize: 11, fill: t.axisLabel, fontFamily: 'Inter, system-ui, sans-serif' };
  const gridStyle = { strokeDasharray: '3 3', stroke: t.gridSubtle, strokeOpacity: 1 };

  const renderTable = () => {
    const firstRow = data?.[0];
    if (!data || !firstRow) {
      return (
        <div className="text-center py-12">
          <FaTable className="mx-auto h-10 w-10 text-gray-300 mb-3" />
          <p className="text-gray-400 text-sm">No data to display</p>
        </div>
      );
    }

    const columns = Object.keys(firstRow);

    return (
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search data..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 text-sm transition-all"
            />
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-xs text-gray-400">Show:</label>
            <ThemedSelect
              value={pageSize}
              onChange={(e) => {
                setPageSize(parseInt(String(e.target.value)));
                setCurrentPage(1);
              }}
              className="px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 transition-all"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </ThemedSelect>
          </div>
        </div>

        <div className="text-xs text-gray-400">
          Showing {Math.min((currentPage - 1) * pageSize + 1, processedData.length)}&ndash;
          {Math.min(currentPage * pageSize, processedData.length)} of {processedData.length} entries
          {searchTerm && ` (filtered from ${data.length})`}
        </div>

        <div className="overflow-x-auto rounded-xl border border-gray-200/80 shadow-sm">
          <table className="min-w-full divide-y divide-gray-100">
            <thead>
              <tr className="bg-gray-50/80">
                {columns.map((column) => (
                  <th
                    key={column}
                    onClick={() => handleSort(column)}
                    className="px-5 py-3 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-600 hover:bg-gray-100/50 transition-colors select-none"
                  >
                    <div className="flex items-center space-x-1.5">
                      <span>{column}</span>
                      {getSortIcon(column)}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-surface divide-y divide-gray-50">
              {paginatedData.map((row, index) => (
                <tr key={index} className="hover:bg-primary-50/30 transition-colors">
                  {columns.map((column) => (
                    <td key={column} className="px-5 py-3 whitespace-nowrap text-sm text-gray-700">
                      {formatValue(row[column])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-1">
            <div className="text-xs text-gray-400">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex items-center space-x-1.5">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="flex items-center px-3 py-1.5 rounded-lg text-xs font-medium text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <FaChevronLeft className="mr-1" size={10} />
                Prev
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) pageNum = i + 1;
                else if (currentPage <= 3) pageNum = i + 1;
                else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = currentPage - 2 + i;
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`w-8 h-8 text-xs rounded-lg font-medium transition-all ${
                      currentPage === pageNum
                        ? 'bg-primary-500 text-white shadow-sm shadow-primary-200'
                        : 'text-gray-500 hover:bg-gray-100'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="flex items-center px-3 py-1.5 rounded-lg text-xs font-medium text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                Next
                <FaChevronRight className="ml-1" size={10} />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderLineChart = () => {
    if (!data || data.length === 0) {
      return <div className="text-center py-12 text-gray-400 text-sm">No data to display</div>;
    }

    const { labelKey, numericKeys } = columnAnalysis;
    const rightAxisKeys = asList(chartConfig?.rightAxis);
    const hasRightAxis =
      rightAxisKeys.length > 0 && numericKeys.some((k) => rightAxisKeys.includes(k));
    const leftKeys = numericKeys.filter((k) => !rightAxisKeys.includes(k));

    return (
      <div className="bg-gradient-to-br from-white to-slate-50/50 rounded-2xl border border-gray-200/60 shadow-sm p-6">
        <ResponsiveContainer width="100%" height={420}>
          {/* ComposedChart, not AreaChart: an AreaChart draws only its <Area>s (ST-00260). */}
          <ComposedChart
            data={data}
            margin={{ top: 10, right: hasRightAxis ? 65 : 25, left: 10, bottom: 5 }}
          >
            <defs>
              {numericKeys.map((key, i) => (
                <linearGradient key={key} id={`gradient-${i}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={cycle(PALETTE, i).stroke} stopOpacity={0.4} />
                  <stop offset="95%" stopColor={cycle(PALETTE, i).stroke} stopOpacity={0.03} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid {...gridStyle} />
            <XAxis
              dataKey={labelKey ?? undefined}
              tickFormatter={formatXTick}
              tick={axisStyle}
              axisLine={{ stroke: t.grid }}
              tickLine={false}
              angle={data.length > 15 ? -35 : 0}
              textAnchor={data.length > 15 ? 'end' : 'middle'}
              height={data.length > 15 ? 65 : 35}
              dy={5}
            />
            <YAxis
              yAxisId="left"
              tick={axisStyle}
              axisLine={false}
              tickLine={false}
              tickFormatter={compactTick}
              width={50}
            />
            {hasRightAxis && (
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ ...axisStyle, fill: t.textFaint }}
                axisLine={false}
                tickLine={false}
                tickFormatter={compactTick}
                width={55}
              />
            )}
            <Tooltip
              content={<CustomTooltip labelFormatter={formatXTooltipLabel} />}
              cursor={{ stroke: t.axis, strokeDasharray: '4 4' }}
            />
            <Legend content={<CustomLegend />} />
            {/* Area fills for left-axis series; the tooltip and legend list each series by its line */}
            {leftKeys.map((key) => {
              const i = numericKeys.indexOf(key);
              return (
                <Area
                  key={`area-${key}`}
                  type="monotone"
                  dataKey={key}
                  yAxisId="left"
                  stroke="none"
                  fill={`url(#gradient-${i})`}
                  fillOpacity={1}
                  tooltipType="none"
                  legendType="none"
                />
              );
            })}
            {/* Lines for all series */}
            {numericKeys.map((key) => {
              const i = numericKeys.indexOf(key);
              const isRight = rightAxisKeys.includes(key);
              return (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  yAxisId={isRight ? 'right' : 'left'}
                  stroke={cycle(PALETTE, i).stroke}
                  strokeWidth={isRight ? 2.5 : 3}
                  strokeDasharray={isRight ? '6 3' : undefined}
                  dot={
                    data.length <= 50
                      ? {
                          r: 3,
                          fill: t.surface,
                          stroke: cycle(PALETTE, i).stroke,
                          strokeWidth: 2.5,
                        }
                      : false
                  }
                  activeDot={{
                    r: 6,
                    fill: cycle(PALETTE, i).stroke,
                    stroke: t.surface,
                    strokeWidth: 3,
                  }}
                />
              );
            })}
          </ComposedChart>
        </ResponsiveContainer>
        {hasRightAxis && (
          <div className="flex justify-center gap-6 mt-3 text-[10px] text-gray-400 uppercase tracking-wide">
            <span className="flex items-center gap-1.5">
              <span className="w-5 h-0.5 bg-gray-400 rounded-full" />
              Left axis
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-5 border-b border-dashed border-gray-400" />
              Right axis
            </span>
          </div>
        )}
        <div className="text-center mt-3">
          <span className="text-[10px] text-gray-300 uppercase tracking-widest">
            {data.length} data points
          </span>
        </div>
      </div>
    );
  };

  const renderBarChart = () => {
    if (!data || data.length === 0) {
      return <div className="text-center py-12 text-gray-400 text-sm">No data to display</div>;
    }

    const { labelKey, numericKeys } = columnAnalysis;
    const rightAxisKeys = asList(chartConfig?.rightAxis);
    const hasRightAxis =
      rightAxisKeys.length > 0 && numericKeys.some((k) => rightAxisKeys.includes(k));

    return (
      <div className="bg-gradient-to-br from-white to-slate-50/50 rounded-2xl border border-gray-200/60 shadow-sm p-6">
        <ResponsiveContainer width="100%" height={420}>
          <BarChart
            data={data}
            margin={{ top: 10, right: hasRightAxis ? 65 : 25, left: 10, bottom: 5 }}
            barCategoryGap="20%"
          >
            <defs>
              {numericKeys.map((key, i) => (
                <linearGradient key={key} id={`bar-gradient-${i}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={cycle(PALETTE, i).stroke} stopOpacity={0.9} />
                  <stop offset="100%" stopColor={cycle(PALETTE, i).stroke} stopOpacity={0.6} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid {...gridStyle} vertical={false} />
            <XAxis
              dataKey={labelKey ?? undefined}
              tickFormatter={formatXTick}
              tick={axisStyle}
              axisLine={{ stroke: t.grid }}
              tickLine={false}
              angle={data.length > 10 ? -35 : 0}
              textAnchor={data.length > 10 ? 'end' : 'middle'}
              height={data.length > 10 ? 75 : 35}
              dy={5}
            />
            <YAxis
              yAxisId="left"
              tick={axisStyle}
              axisLine={false}
              tickLine={false}
              tickFormatter={compactTick}
              width={50}
            />
            {hasRightAxis && (
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ ...axisStyle, fill: t.textFaint }}
                axisLine={false}
                tickLine={false}
                tickFormatter={compactTick}
                width={55}
              />
            )}
            <Tooltip
              content={<CustomTooltip labelFormatter={formatXTooltipLabel} />}
              cursor={{ fill: t.cursor }}
            />
            <Legend content={<CustomLegend />} />
            {numericKeys.map((key, i) => (
              <Bar
                key={key}
                dataKey={key}
                yAxisId={rightAxisKeys.includes(key) ? 'right' : 'left'}
                fill={`url(#bar-gradient-${i})`}
                radius={[6, 6, 0, 0]}
                maxBarSize={48}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
        {hasRightAxis && (
          <div className="flex justify-center gap-6 mt-3 text-[10px] text-gray-400 uppercase tracking-wide">
            <span>
              Left axis: {numericKeys.filter((k) => !rightAxisKeys.includes(k)).join(', ')}
            </span>
            <span className="text-gray-300">|</span>
            <span>
              Right axis: {numericKeys.filter((k) => rightAxisKeys.includes(k)).join(', ')}
            </span>
          </div>
        )}
        <div className="text-center mt-3">
          <span className="text-[10px] text-gray-300 uppercase tracking-widest">
            {data.length} data points
          </span>
        </div>
      </div>
    );
  };

  const renderPieChart = () => {
    if (!data || data.length === 0) {
      return <div className="text-center py-12 text-gray-400 text-sm">No data to display</div>;
    }

    const { labelKey, numericKeys } = columnAnalysis;
    const valueKey = numericKeys[0];

    // Calculate total for center label
    const total = data.reduce(
      (sum, row) => sum + (valueKey !== undefined ? Number(row[valueKey]) || 0 : 0),
      0
    );

    const renderLabel = ({ cx, cy, midAngle, outerRadius, percent, name }: PieLabelProps) => {
      if (percent < 0.04) return null; // skip tiny slices
      const RADIAN = Math.PI / 180;
      const radius = outerRadius + 22;
      const x = cx + radius * Math.cos(-midAngle * RADIAN);
      const y = cy + radius * Math.sin(-midAngle * RADIAN);
      return (
        <text
          x={x}
          y={y}
          textAnchor={x > cx ? 'start' : 'end'}
          dominantBaseline="central"
          className="fill-gray-500"
          style={{ fontSize: 11, fontFamily: 'Inter, system-ui, sans-serif' }}
        >
          {formatXTick(name)} ({(percent * 100).toFixed(0)}%)
        </text>
      );
    };

    return (
      <div className="bg-gradient-to-br from-white to-slate-50/50 rounded-2xl border border-gray-200/60 shadow-sm p-6">
        <ResponsiveContainer width="100%" height={420}>
          <PieChart>
            <defs>
              {data.map((_, i) => (
                <linearGradient key={i} id={`pie-gradient-${i}`} x1="0" y1="0" x2="1" y2="1">
                  <stop
                    offset="0%"
                    stopColor={PIE_COLORS[i % PIE_COLORS.length]}
                    stopOpacity={0.95}
                  />
                  <stop
                    offset="100%"
                    stopColor={PIE_COLORS[i % PIE_COLORS.length]}
                    stopOpacity={0.7}
                  />
                </linearGradient>
              ))}
            </defs>
            <Pie
              data={data}
              dataKey={valueKey ?? ''}
              nameKey={labelKey ?? undefined}
              cx="50%"
              cy="50%"
              innerRadius={70}
              outerRadius={140}
              paddingAngle={2}
              label={renderLabel}
              labelLine={{ stroke: t.axis, strokeWidth: 1 }}
              stroke={t.surface}
              strokeWidth={2}
            >
              {data.map((_, index) => (
                <Cell
                  key={index}
                  fill={`url(#pie-gradient-${index})`}
                  style={{ filter: 'drop-shadow(0 1px 2px rgb(var(--shadow-rgb) / 0.06))' }}
                />
              ))}
            </Pie>
            {/* Center label */}
            <text
              x="50%"
              y="47%"
              textAnchor="middle"
              dominantBaseline="central"
              className="fill-gray-800"
              style={{ fontSize: 22, fontWeight: 700, fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              {total >= 1000 ? (total / 1000).toFixed(1) + 'k' : total.toLocaleString()}
            </text>
            <text
              x="50%"
              y="55%"
              textAnchor="middle"
              dominantBaseline="central"
              className="fill-gray-400"
              style={{
                fontSize: 10,
                fontFamily: 'Inter, system-ui, sans-serif',
                textTransform: 'uppercase',
                letterSpacing: 1,
              }}
            >
              Total
            </text>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
        {/* Compact legend below */}
        <div className="flex flex-wrap justify-center gap-x-4 gap-y-1.5 mt-2 px-4">
          {data.map((row, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <span
                className="w-2.5 h-2.5 rounded-sm"
                style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }}
              />
              <span className="text-xs text-gray-500">
                {formatXTick(labelKey ? row[labelKey] : undefined)}
              </span>
            </div>
          ))}
        </div>
        <div className="text-center mt-3">
          <span className="text-[10px] text-gray-300 uppercase tracking-widest">
            {data.length} segments
          </span>
        </div>
      </div>
    );
  };

  const renderVisualization = () => {
    switch (visualizationType) {
      case 'line':
        return renderLineChart();
      case 'chart':
      case 'bar':
        return renderBarChart();
      case 'pie':
        return renderPieChart();
      case 'table':
      default:
        return renderTable();
    }
  };

  return (
    <div className="space-y-4">
      {title && (
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100/80 text-xs text-gray-400">
            {visualizationType === 'table' ? (
              <FaTable size={11} />
            ) : visualizationType === 'line' ? (
              <FaChartLine size={11} />
            ) : visualizationType === 'pie' ? (
              <FaChartPie size={11} />
            ) : (
              <FaChartBar size={11} />
            )}
            <span className="capitalize">{visualizationType}</span>
          </div>
        </div>
      )}
      {renderVisualization()}
    </div>
  );
};

export default DataVisualization;
